import { NavigateFunction } from 'react-router';
import { z } from 'zod';

import { Turbine } from './types';
import { DataTableColumn } from '@/components/DataTable';
import { adminToolPaths } from '@/constants/adminTool';

const StatusIndicator = ({ turbine }: { turbine: Turbine }) => {
  const getStatus = (turbine: Turbine) => {
    const isActive = turbine.isActive;

    if (isActive) {
      return { text: 'Active', color: 'var(--color-green)' };
    } else {
      return { text: 'Inactive', color: 'var(--color-orange)' };
    }
  };

  const status = getStatus(turbine);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span>{status?.text}</span>
      <div
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: status?.color
        }}
      />
    </div>
  );
};

export const createTurbinesColumns = (
  navigate: NavigateFunction
): DataTableColumn[] => [
  {
    field: 'id',
    headerName: 'SERIAL NUMBER',
    width: 'auto',
    sortable: true,
    onClick: row => {
      navigate(adminToolPaths.turbines.detail.replace(':turbineId', row.id));
    }
  },
  {
    field: 'name',
    headerName: 'NAME',
    width: 'auto',
    sortable: true
  },
  {
    field: 'subType',
    headerName: 'MODEL',
    width: 'auto',
    sortable: true,
    renderCell: ({ row }) => row.subType
  },
  {
    field: 'maxCapacityMW',
    headerName: 'MAX CAPACITY MW',
    width: 'auto',
    sortable: true,
    valueGetter: (_, row) => parseFloat(row.maxCapacityMW) || 0,
    renderCell: ({ row }) => row.maxCapacityMW
  },
  {
    field: 'isActive',
    headerName: 'STATUS',
    width: 'auto',
    sortable: true,
    valueGetter: (_, row) => (row.isActive ? 'Active' : 'Inactive'),
    renderCell: ({ row }) => <StatusIndicator turbine={row} />
  }
];

export const createTurbineSchema = z.object({
  serialNumber: z.string().min(1, 'The serial number is required'),
  name: z.string().min(1, 'The name is required'),
  brand: z.string().min(1, 'The brand is required')
});

export const editTurbineSchema = z.object({
  serialNumber: z.string().optional(),
  name: z.string().min(1, 'The name is required'),
  brand: z.string().min(1, 'The brand is required')
});

export const turbineSchema = createTurbineSchema;

export const parseTurbineSchema = (parsedData: any) => {
  const errors: { [k: string]: string } = {};
  parsedData.error.errors.forEach(
    (e: { path: { toString: () => string | number }[]; message: string }) => {
      if (e.path[0]) errors[e.path[0].toString()] = e.message;
    }
  );
  return errors;
};

export const filterTurbines = (
  turbines: Turbine[],
  searchTerm: string,
  showActive: boolean,
  showInactive: boolean
): Turbine[] => {
  return turbines.filter((turbine: Turbine) => {
    const matchesSearch =
      !searchTerm ||
      turbine.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      turbine.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      turbine.subType?.toLowerCase().includes(searchTerm.toLowerCase());

    const isActive = turbine.isActive;
    const matchesStatus =
      (showActive && isActive) || (showInactive && !isActive);

    return matchesSearch && matchesStatus;
  });
};

export const modelOptions = ['SMT60', 'SMT130', 'MPU60', 'M35'];

export const turbineTypeOptions = ['Natural Gas', 'Dual Fuel'];
